import {fetch} from 'coreUtils/utils';
import {getAppOrigin, isHybridActive} from 'coreUtils/url';
import {getConfig} from 'coreUtils/utils';
import {isServer, timeout} from 'coreUtils/utils';

import ManagerRepository from 'coreClasses/ManagerRepository';
import type {
	TApplePayResponseValidationEvent,
	TApplePayContactValidationResult,
} from 'coreClasses/applepay/types/TApplePayAPI';
import type {ApplePayJS as TApplePayJS} from 'coreClasses/applepay/types/TApplePayWebAPI';
import DeviceMgr from 'coreClasses/device/DeviceMgr';
import {INewsletterPreferences} from 'coreClasses/customer/ICustomer';

import {
	RouteInfo,
	SFCCAPIConfig,
	CSRFToken,
	LogLevel,
	LogType,
	CreateReservationRequest,
	FindAvailableStoresRequest,
	FindAvailableStoresResponse,
} from './interfaces';

/**
 * Represents SFCCAPI
 */
class SFCCAPI {
	private config: SFCCAPIConfig = null;
	private csrfToken: CSRFToken = null;
	private csrfTokenPromise: Promise<CSRFToken> = null;
	private currentLocaleID: string = 'default';
	private currentCountryCode: string;
	private managerRepository: ManagerRepository;
	private currentFetchPromises = [];
	public isCORSRequest: boolean;

	/**
	 * Create an instance of the SFCCAPI
	 * @param {ManagerRepository} managerRepository - ManagerRepository instance
	 */
	constructor(managerRepository: ManagerRepository) {
		this.isCORSRequest = managerRepository.getUiMgr().isCORSRequest();
		this.managerRepository = managerRepository;
	}

	/**
	 * Returns current config
	 * @returns {Object}
	 */
	private getConfig() {
		if (!this.config) {
			const {app: appConfig} = getConfig();
			this.config = {...appConfig.sfccAPI};
		}

		return this.config;
	}

	/**
	 * Sets locale for API requests
	 * @param {String} locale Locale to set
	 * @param {String} country Country to set
	 */
	public setLocale(locale?: string, country?: string) {
		this.currentLocaleID = locale || 'default';
		this.currentCountryCode = country || locale?.split('-')[1] || this.currentCountryCode || 'IT';
	}

	/**
	 * Sets country for API requests
	 * @param {String} countryCode country to set
	 */
	public setCountry(countryCode?: string) {
		this.currentCountryCode = countryCode ?? 'IT';
	}

	/**
	 * Gets site id
	 * @returns {string} - siteId
	 */
	public getSiteId() {
		return this.getConfig().siteId;
	}

	/**
	 * Sets site id for API requests
	 * @param {String} siteId Site id to set
	 */
	public setSiteId(siteId?: string) {
		if (this.getConfig() && siteId) {
			this.config.siteId = siteId;
		}
	}

	/**
	 * Generates the URL to call based on provided options
	 * @param {String} path The endpoint path
	 * @param {Boolean} isController if TRUE the URL is generated with the Controller-Action format, otherwise it is generated with the /s/<siteId>/path format
	 * @returns {String}
	 */
	public generateEndpointUrl(path: string, isController: boolean): string {
		if (isController) {
			return `/on/demandware.store/Sites-${this.getConfig().siteId}-Site/${this.currentLocaleID}/${path}`;
		} else {
			const sitePath = `/s/${this.getConfig().siteId}`;

			return this.getConfig().siteId + '/' + path.startsWith(this.getConfig().pathPrefix)
				? `${sitePath}${path}`
				: `${this.getConfig().pathPrefix}${sitePath}${path}`;
		}
	}

	/**
	 * Generates full URL to use in redirects or legacy calls
	 * @param path The endpoint path
	 * @param isController if TRUE the URL is generated with the Controller-Action format, otherwise it is generated with the /s/<siteId>/path format
	 * @param cached Chooses if the call should be made to the cached endpoint or not. The caching happens on the CDN level, this means that the session is not applied to this kind of calls.
	 * @return string
	 */
	public generateFullUrl(path: string, isController: boolean, cached: boolean = false) {
		const proxyPath = cached ? this.config.cachedProxyPath : this.config.proxyPath;

		return `${getAppOrigin()}${proxyPath}${this.generateEndpointUrl(path, isController)}`;
	}

	/**
	 * Executes a POST call
	 * @param {String} endpoint Endpoint to be called
	 * @param {Object} payload Payload to be attached to the call
	 * @param {Object} queryParams Query params attached to the URL
	 * @param {Boolean} applyCSRF Defines if CSRF protection token should be applied
	 * @returns {Promise<Any>}
	 * @protected
	 */
	private async post(endpoint: string, payload?: object, queryParams: object = {}, applyCSRF: boolean = true) {
		return this.fetch(endpoint, 'POST', queryParams, payload, applyCSRF);
	}

	/**
	 * Executes a GET call
	 * @param {String} endpoint Endpoint to be called
	 * @param {Object} queryParams Query params attached to the URL
	 * @param {Boolean} cached Chooses if the call should be made to the cached endpoint or not. The caching happens on the CDN level, this means that the session is not applied to this kind of calls
	 * @param {Boolean} applyCsrf Chooses if the call should apply CSRF protection or not.
	 * @returns {Promise<Any>}
	 * @private
	 */
	private async get(endpoint: string, queryParams: object = {}, cached: boolean = false, applyCsrf: boolean = false) {
		return this.fetch(endpoint, 'GET', queryParams, null, applyCsrf, cached);
	}

	/**
	 * @swagger
	 * /CSRF-Generate:
	 *   post:
	 *     summary: Returns the current CSRF token
	 *     description: If no token is stored, it fetches a new token. If a request is already in progress, its promise is returned.
	 *     responses:
	 *       200:
	 *         description: Success
	 */
	private async getCSRFToken(): Promise<CSRFToken> {
		if (this.csrfTokenPromise) {
			return this.csrfTokenPromise;
		} else {
			return (this.csrfTokenPromise = new Promise<CSRFToken>((resolve) => {
				if (!this.csrfToken) {
					this.fetch(this.generateEndpointUrl('CSRF-Generate', true), 'POST')
						.then((res) => res.json())
						.then((res) => {
							this.csrfToken = res.csrf;
							this.csrfTokenPromise = null;

							resolve(this.csrfToken);
						});
				} else {
					this.csrfTokenPromise = null;

					resolve(this.csrfToken);
				}
			}));
		}
	}

	/**
	 * Calls the service endpoint
	 * @param {String} originalUrl Url to call
	 * @param {String} method HTTP method to be used
	 * @param {Object} originalQueryParams Query params attached to the URL
	 * @param {Object} payload Payload to be attached to the call
	 * @param {Boolean} applyCSRF If true, the CSRF token is applied to the call according to SFCC specifications
	 * @param {Boolean} cached Chooses if the call should be made to the cached endpoint or not. The caching happens on the CDN level, this means that the session is not applied to this kind of calls. Applies only to GET method.
	 * @returns {Promise<Any>}
	 * @private
	 */
	private async fetch(
		originalUrl: string,
		method: string,
		originalQueryParams: object = {},
		payload: object = {},
		applyCSRF: boolean = false,
		cached: boolean = false,
	) {
		if (!isServer && !cached) {
			/**
			 * Making calls to SFCC custom APIs when refresh token doesn't exists can cause session bridge inconsistency
			 */
			while (!(this.managerRepository.getCommerceApi() as any).auth.refreshToken) {
				await timeout(250);
			}

			/**
			 * If session guard cookie doesn't exist we need to be sure that only one call will create a new session bridge.
			 * So we make one call per time.
			 */
			if (!(this.managerRepository.getCommerceApi() as any).auth.sessionGuard && this.currentFetchPromises.length > 0) {
				await Promise.all(this.currentFetchPromises);
			}
		}

		let repeat = false;
		let response;
		let loopCount = 0;
		const appOrigin = getAppOrigin();

		const isPreview = this.managerRepository.getContentMgr().isPreviewMode();
		const canBeCached = cached && !isPreview && !applyCSRF;

		do {
			const proxyPath = canBeCached ? this.getConfig().cachedProxyPath : this.getConfig().proxyPath;
			let url = originalUrl.startsWith('http')
				? originalUrl
				: `${appOrigin}${proxyPath}/${originalUrl.replace(/^\//g, '')}`;
			let queryParams = Object.assign({}, originalQueryParams);

			if (applyCSRF) {
				const csrfToken = await this.getCSRFToken();
				queryParams[csrfToken.tokenName] = csrfToken.token;
			}

			const appOriginHost = new URL(appOrigin).host;

			switch (method) {
				case 'GET':
					if (!isHybridActive()) {
						Object.assign(queryParams, payload, {cookieDomain: appOriginHost});
					}

					break;
				case 'POST':
					cached = false;

					break;
			}

			if (Object.keys(queryParams).length > 0) {
				url = `${url}?${generateQueryString(queryParams)}`;
			}

			const headers = {Accept: 'application/json'};

			if (!canBeCached) {
				headers['cache-control'] = 'no-cache';
			}

			if (isServer || (window.navigator && DeviceMgr.isBot(window.navigator.userAgent))) {
				headers['x-custom-nobridge'] = 'true';
			}

			if (isPreview) {
				headers['x-preview'] = 'true';
			}

			const options = {
				method: method,
				headers,
				body: payload ? JSON.stringify(payload) : undefined,
			};

			if (this.isCORSRequest) {
				options['credentials'] = 'include';
			}

			const responsePromise = fetch(url, options);

			const promiseIndex = this.currentFetchPromises.push(responsePromise) - 1;

			response = await responsePromise;

			this.currentFetchPromises.splice(promiseIndex, 1);

			if (response.status > 209) {
				this.csrfTokenPromise = null;
				this.csrfToken = null;

				repeat = !!(response.status === 500 && response.url.includes('CSRF-AjaxFail'));
			} else {
				if (applyCSRF) {
					this.csrfTokenPromise = null;
					this.csrfToken = null;
				}

				repeat = false;
			}

			loopCount++;
		} while (repeat && loopCount <= 1);

		return response;
	}

	/**
	 * @swagger
	 * /Configurations-Get:
	 *   get:
	 *     summary: Gets all configurations
	 *     description: Gets all configurations
	 *     responses:
	 *       200:
	 *         description: Success
	 */
	async getConfigurations(): Promise<any> {
		return await this.get(this.generateEndpointUrl('Configurations-Get', true), {}, true).then(
			(response: Response) => response.json(),
		);
	}

	/**
	 * @swagger
	 * /Countries-Get:
	 *   get:
	 *     summary: Gets a single country
	 *     description: Gets a single country
	 *     parameters:
	 *       - name: id
	 *         in: query
	 *         description: CountryCode to be searched
	 *         required: true
	 *         schema:
	 *           type: string
	 *     responses:
	 *       200:
	 *         description: Success
	 */
	async getCountry(countryCode: string): Promise<any> {
		return await this.get(this.generateEndpointUrl('Countries-Get', true), {id: countryCode}, true);
	}

	/**
	 * @swagger
	 * /Customer-Exist:
	 *   post:
	 *     summary: Checks if customer exist
	 *     description: Checks if customer exist
	 *     security:
	 *       - csrf: []
	 *     requestBody:
	 *       content:
	 *         application/json:
	 *           schema:
	 *             required:
	 *               - email
	 *             type: object
	 *             properties:
	 *               email:
	 *                 type: string
	 *                 example: test@test.com
	 *     responses:
	 *       200:
	 *         description: Success
	 */
	async checkIfExistCustomer(email: string): Promise<any> {
		return await this.post(this.generateEndpointUrl('Customer-Exist', true), {
			email: email,
		}).then((response: Response) => response.json());
	}

	/**
	 * @swagger
	 * /Newsletter-Subscribe:
	 *   post:
	 *     summary: Subscribes user to newsletter
	 *     description: Subscribes user to newsletter
	 *     security:
	 *       - csrf: []
	 *     requestBody:
	 *       content:
	 *         application/json:
	 *           schema:
	 *             required:
	 *               - email
	 *               - countryCode
	 *               - languageCode
	 *             type: object
	 *             properties:
	 *               email:
	 *                 type: string
	 *                 example: test@test.com
	 *               gender:
	 *                 type: string
	 *                 example: Female
	 *               countryCode:
	 *                 type: string
	 *                 example: US
	 *               languageCode:
	 *                 type: string
	 *                 example: en
	 *               subscriptions:
	 *                 type: string[]
	 *                 example: ['STORE', 'RACES']
	 *     responses:
	 *       200:
	 *         description: Success
	 */
	async subscribeToNewsletter(
		email: string,
		gender: string,
		source?: string,
		subscriptions?: string[],
		authorizationDataCollection = null,
		acceptsMarketing = null,
	): Promise<any> {
		return await this.post(this.generateEndpointUrl('Newsletter-Subscribe', true), {
			email: email,
			gender: gender || '',
			countryCode: this.currentCountryCode,
			languageCode: this.currentLocaleID,
			source: source,
			subscriptions: subscriptions,
			authorizationDataCollection: authorizationDataCollection,
			acceptsMarketing: acceptsMarketing,
		}).then((response: Response) => response.json());
	}

	/**
	 * @swagger
	 * /Newsletter-Unsubscribe:
	 *   post:
	 *     summary: Unsubscribes user to newsletter
	 *     description: Unsubscribes user to newsletter
	 *     security:
	 *       - csrf: []
	 *     requestBody:
	 *       content:
	 *         application/json:
	 *           schema:
	 *             required:
	 *               - email
	 *             type: object
	 *             properties:
	 *               email:
	 *                 type: string
	 *                 example: test@test.com
	 *               unsubscribeReason:
	 *                 type: string
	 *                 example: No more interesting
	 *     responses:
	 *       200:
	 *         description: Success
	 */
	async unsubscribeToNewsletter(email: string, unsubscribeReason: string): Promise<any> {
		return await this.post(this.generateEndpointUrl('Newsletter-Unsubscribe', true), {
			email: email,
			unsubscribeReason: unsubscribeReason || '',
		}).then((response: Response) => response.json());
	}

	/**
	 * @swagger
	 * /Newsletter-UpdateProfile:
	 *   post:
	 *     summary: Update customer newsletter profile
	 *     description: Update customer newsletter profile
	 *     security:
	 *       - csrf: []
	 *     requestBody:
	 *       content:
	 *         application/json:
	 *           schema:
	 *             required:
	 *               - email
	 *             type: object
	 *             properties:
	 *               email:
	 *                 type: string
	 *                 example: test@test.com
	 *               gender:
	 *                 type: string
	 *                 example: Female
	 *               countryCode:
	 *                 type: string
	 *                 example: US
	 *               languageCode:
	 *                 type: string
	 *                 example: en
	 *     responses:
	 *       200:
	 *         description: Success
	 */
	async updateNewsletterProfile(email: string, preferences: INewsletterPreferences): Promise<any> {
		return await this.post(this.generateEndpointUrl('Newsletter-UpdateProfile', true), {
			email: email,
			countryCode: this.currentCountryCode,
			languageCode: this.currentLocaleID,
			...preferences,
		}).then((response: Response) => response.json());
	}

	/**
	 * @swagger
	 * /Newsletter-GetProfile:
	 *   post:
	 *     summary: Retrieve newsletter profile by email
	 *     description: Retrieve newsletter profile by email
	 *     security:
	 *       - csrf: []
	 *     requestBody:
	 *       content:
	 *         application/json:
	 *           schema:
	 *             required:
	 *               - email
	 *             type: object
	 *             properties:
	 *               email:
	 *                 type: string
	 *                 example: test@test.com
	 *     responses:
	 *       200:
	 *         description: Success
	 */
	async getNewsletterProfile(email: string): Promise<any> {
		return await this.get(this.generateEndpointUrl('Newsletter-GetProfile', true), {
			email: email,
		}).then((response: Response) => response.json());
	}

	/**
	 * @swagger
	 * /Newsletter-CreateProfile:
	 *   post:
	 *     summary: Create profile in the newsletter provider
	 *     description: Create profile in the newsletter provider
	 *     security:
	 *       - csrf: []
	 *     requestBody:
	 *       content:
	 *         application/json:
	 *           schema:
	 *             required:
	 *               - email
	 *               - countryCode
	 *               - languageCode
	 *             type: object
	 *             properties:
	 *               email:
	 *                 type: string
	 *                 example: test@test.com
	 *               countryCode:
	 *                 type: string
	 *                 example: US
	 *               languageCode:
	 *                 type: string
	 *                 example: en
	 *     responses:
	 *       200:
	 *         description: Success
	 */
	async createProfile(email: string, countryCode: string, languageCode: string): Promise<any> {
		return await this.post(this.generateEndpointUrl('Newsletter-CreateProfile', true), {
			email: email,
			countryCode: countryCode,
			languageCode: languageCode,
		}).then((response: Response) => response.json());
	}

	/**
	 * @swagger
	 * /Loyalty-Join:
	 *   post:
	 *     summary: Joins user to loyalty program
	 *     description: Joins user to loyalty program
	 *     security:
	 *       - csrf: []
	 *     responses:
	 *       200:
	 *         description: Success
	 */
	async joinToLoyalty(): Promise<any> {
		return await this.post(this.generateEndpointUrl('Loyalty-Join', true), {}).then((response: Response) =>
			response.json(),
		);
	}

	/**
	 * @swagger
	 * /NotifyMe-Create:
	 *   post:
	 *     summary: Creates a new NotifyMe record.
	 *     description: If a record for the provided email already exists, it is updated.
	 *     security:
	 *       - csrf: []
	 *     requestBody:
	 *       content:
	 *         application/json:
	 *           schema:
	 *             required:
	 *               - email
	 *               - products
	 *             type: object
	 *             properties:
	 *               email:
	 *                 type: string
	 *                 example: test@test.com
	 *               products:
	 *                 type: array
	 *                 items:
	 *                   type: string
	 *                   example: 'aly0250004blk'
	 *     responses:
	 *       200:
	 *         description: Success
	 */
	async createNotifyMe(email: string, productId: string, languageCode: string, countryCode: string): Promise<any> {
		return await this.post(this.generateEndpointUrl('NotifyMe-Create', true), {
			email,
			productId: productId,
			languageCode,
			countryCode,
		});
	}

	/**
	 * Gets the routing information about the provided path.
	 * @param {String} path The path for which we are requesting information
	 * @param query object with parameters
	 * @returns {Promise<RouteInfo>}
	 */
	async getRouteInfo(path: string, query: object): Promise<RouteInfo> {
		return await this.get(this.generateEndpointUrl(path, false), {routeInfo: true, ...query}, true).then(
			(response: Response) => {
				let redirectsStack = response.headers.get('redirects-stack');

				if (redirectsStack) {
					try {
						redirectsStack = JSON.parse(redirectsStack).map((r: {location: string; status: number}) => {
							const loc = new URL(r.location);

							loc.searchParams.delete('country');
							loc.searchParams.delete('routeInfo');
							loc.searchParams.delete('cookieDomain');

							return {
								location: {
									href: loc.href,
									pathname: loc.pathname,
									search: loc.search,
								},
								status: r.status,
							};
						});
					} catch (e) {
						redirectsStack = null;
					}
				}

				return response.text().then((text) => {
					let json;

					try {
						json = JSON.parse(text);
					} catch (e) {
						json = {
							action: 'Home-ErrorNotFound',
							jsonld: [],
						};
					}

					return redirectsStack ? {...json, redirectsStack} : json;
				});
			},
		);
	}

	/**
	 * @swagger
	 * /Context-Get:
	 *   get:
	 *     summary: Retrieves user session attributes
	 *     description: Retrieves user session attributes
	 *     responses:
	 *       200:
	 *         description: Success
	 */
	async getContext(): Promise<any> {
		return await this.get(this.generateEndpointUrl('Context-Get', true), {}, false).then((response: Response) =>
			response.json(),
		);
	}

	/**
	 * @swagger
	 * /Context-Set:
	 *   post:
	 *     summary: Sets session values
	 *     description: Sets session values
	 *     requestBody:
	 *       description: object with key->value pairs
	 *       content:
	 *         application/json:
	 *           schema:
	 *             required:
	 *               - email
	 *               - products
	 *             type: object
	 *             properties:
	 *               customQualifiers:
	 *                 type: object
	 *                 example: {}
	 *     responses:
	 *       200:
	 *         description: Success
	 */
	async setContext(payload: object): Promise<any> {
		return await this.post(this.generateEndpointUrl('Context-Set', true), payload, {}, false).then(
			(response: Response) => response.json(),
		);
	}

	/**
	 * @swagger
	 * /Basket-Init:
	 *   get:
	 *     summary: Inits the Basket on SFCC instance
	 *     responses:
	 *       200:
	 *         description: Success
	 */
	async initBasket(basketId: string): Promise<any> {
		return await this.get(this.generateEndpointUrl('Basket-Init', true), {basketId}, false).then(
			(response: Response) => response.json(),
		);
	}

	/**
	 * @swagger
	 * /Context-GetCustomerGroups:
	 *   get:
	 *     summary: Retrieves customer groups
	 *     description: Retrieves customer groups
	 *     responses:
	 *       200:
	 *         description: Success
	 */
	async getCustomerGroups(): Promise<any> {
		return await this.get(this.generateEndpointUrl('Context-GetCustomerGroups', true), {}, false).then(
			(response: Response) => response.json(),
		);
	}

	/**
	 * @swagger
	 * /Content-GetCMSBackupData:
	 *   get:
	 *     summary: Retrieves backup content
	 *     description: Retrieves backup content
	 *     parameters:
	 *       - name: id
	 *         in: query
	 *         description: content id
	 *         required: true
	 *         schema:
	 *           type: string
	 *     responses:
	 *       200:
	 *         description: Success
	 */
	async getCMSBackupData(queryParams): Promise<any> {
		return await this.get(this.generateEndpointUrl('Content-GetCMSBackupData', true), queryParams, false).then(
			(response: Response) => response.json(),
		);
	}

	/**
	 * @swagger
	 * /Customer-CheckTokenValidity:
	 *   post:
	 *     summary: Check reset password token validity
	 *     description: Check reset password token validity
	 *     security:
	 *       - csrf: []
	 *     requestBody:
	 *       content:
	 *         application/json:
	 *           schema:
	 *             required:
	 *               - token
	 *             type: object
	 *             properties:
	 *               token:
	 *                 type: string
	 *     responses:
	 *       200:
	 *         description: Success
	 */
	async checkTokenValidity(data: {token: string}): Promise<any> {
		return await this.post(this.generateEndpointUrl('Customer-CheckTokenValidity', true), {
			token: data.token,
		}).then((response: Response) => response.json());
	}

	/**
	 * @swagger
	 * /Customer-SetNewPassword:
	 *   post:
	 *     summary: Sets a new password by reset token
	 *     description: Sets a new password by reset token
	 *     security:
	 *       - csrf: []
	 *     requestBody:
	 *       content:
	 *         application/json:
	 *           schema:
	 *             required:
	 *               - token
	 *               - password
	 *             type: object
	 *             properties:
	 *               token:
	 *                 type: string
	 *               password:
	 *                 type: string
	 *     responses:
	 *       200:
	 *         description: Success
	 */
	async setNewPasswordByToken(data: {token: string; password: string}): Promise<any> {
		return await this.post(this.generateEndpointUrl('Customer-SetNewPassword', true), {
			token: data.token,
			password: data.password,
		}).then((response: Response) => response.json());
	}

	/**
	 * @swagger
	 * /Customer-ResetPassword:
	 *   post:
	 *     summary: Resets password for customer
	 *     description: Resets password for customer
	 *     security:
	 *       - csrf: []
	 *     requestBody:
	 *       content:
	 *         application/json:
	 *           schema:
	 *             required:
	 *               - email
	 *               - countryCode
	 *               - languageCode
	 *             type: object
	 *             properties:
	 *               email:
	 *                 type: string
	 *                 example: test@test.com
	 *               countryCode:
	 *                 type: string
	 *                 example: US
	 *               languageCode:
	 *                 type: string
	 *                 example: en
	 *     responses:
	 *       200:
	 *         description: Success
	 */
	async resetPassword(email: string): Promise<any> {
		return await this.post(this.generateEndpointUrl('Customer-ResetPassword', true), {
			email: email,
			countryCode: this.currentCountryCode,
			languageCode: this.currentLocaleID,
		}).then((response: Response) => response.json());
	}

	/**
	 * @swagger
	 * /__SYSTEM__ApplePay-Cancel:
	 *   post:
	 *     summary: Executes hook Cancel
	 *     description: Executes hook Cancel
	 *     responses:
	 *       200:
	 *         description: Success
	 */
	async applePayCancel(): Promise<any> {
		return await this.post(this.generateEndpointUrl('__SYSTEM__ApplePay-Cancel', true)).then((response: Response) =>
			response.json(),
		);
	}

	/**
	 * @swagger
	 * /__SYSTEM__ApplePay-GetRequest:
	 *   get:
	 *     summary: Executes hook GetRequest
	 *     description: Executes hook GetRequest
	 *     responses:
	 *       200:
	 *         description: Success
	 */
	async applePayGetRequest(): Promise<any> {
		return await this.get(this.generateEndpointUrl('__SYSTEM__ApplePay-GetRequest', true)).then(
			(response: Response) => response.json(),
		);
	}

	/**
	 * @swagger
	 * /__SYSTEM__ApplePay-ShippingContactSelected:
	 *   post:
	 *     summary: Executes hook ShippingContactSelected
	 *     description: Executes hook ShippingContactSelected
	 *     requestBody:
	 *       content:
	 *         application/json:
	 *           schema:
	 *             type: object
	 *     responses:
	 *       200:
	 *         description: Success
	 */
	async applePayOnShippingContactSelected(eventObj): Promise<{
		lineItems: Array<TApplePayJS.ApplePayLineItem>;
		total: TApplePayJS.ApplePayLineItem;
		shippingMethods: Array<TApplePayJS.ApplePayShippingMethod>;
		event: TApplePayResponseValidationEvent;
	}> {
		return await this.post(
			this.generateEndpointUrl('__SYSTEM__ApplePay-ShippingContactSelected', true),
			convertApplePayEventObject(eventObj),
		).then((response: Response) => response.json());
	}

	/**
	 * @swagger
	 * /__SYSTEM__ApplePay-PaymentMethodSelected:
	 *   post:
	 *     summary: Executes hook PaymentMethodSelectedSelected
	 *     description: Executes hook PaymentMethodSelectedSelected
	 *     requestBody:
	 *       content:
	 *         application/json:
	 *           schema:
	 *             type: object
	 *     responses:
	 *       200:
	 *         description: Success
	 */
	async applePayOnPaymentMethodSelected(
		eventObj,
	): Promise<{lineItems: Array<TApplePayJS.ApplePayLineItem>; total: TApplePayJS.ApplePayLineItem}> {
		return await this.post(
			this.generateEndpointUrl('__SYSTEM__ApplePay-PaymentMethodSelected', true),
			convertApplePayEventObject(eventObj),
		).then((response: Response) => response.json());
	}
	/**
	 * @swagger
	 * /__SYSTEM__ApplePay-ValidateMerchant:
	 *   post:
	 *     summary: Executes hook ValidateMerchant
	 *     description: Executes hook ValidateMerchant
	 *     responses:
	 *       200:
	 *         description: Success
	 */
	async applePayOnValidateMerchant(eventObj): Promise<{session: TApplePayJS.ApplePayMerchantSession}> {
		return await this.post(
			this.generateEndpointUrl('__SYSTEM__ApplePay-ValidateMerchant', true),
			convertApplePayEventObject(eventObj),
		).then((response: Response) => response.json());
	}

	/**
	 * @swagger
	 * /__SYSTEM__ApplePay-PaymentAuthorized:
	 *   post:
	 *     summary: Executes hook PaymentAuthorized
	 *     description: Executes hook PaymentAuthorized
	 *     requestBody:
	 *       content:
	 *         application/json:
	 *           schema:
	 *             type: object
	 *     responses:
	 *       200:
	 *         description: Success
	 */
	async applePayOnPaymentAuthorized(eventObj): Promise<{redirect: string} | {error: string; status: string}> {
		return await this.post(
			this.generateEndpointUrl('__SYSTEM__ApplePay-PaymentAuthorized', true),
			convertApplePayEventObject(eventObj),
		).then((response: Response) => response.json());
	}

	/**
	 * @swagger
	 * /__SYSTEM__ApplePay-PrepareBasket:
	 *   post:
	 *     summary: Executes hook PrepareBasket
	 *     description: Executes hook PrepareBasket
	 *     requestBody:
	 *       content:
	 *         application/json:
	 *           schema:
	 *             required:
	 *               - sku
	 *             type: object
	 *             properties:
	 *               sku:
	 *                 type: string
	 *                 description: Product SKU
	 *     responses:
	 *       200:
	 *         description: Success
	 */
	async applePayPrepareBasket(sku): Promise<any> {
		return await this.post(this.generateEndpointUrl('__SYSTEM__ApplePay-PrepareBasket', true), {
			sku,
		}).then((response: Response) => response.json());
	}

	/**
	 * @swagger
	 * /__SYSTEM__ApplePay-ShippingMethodSelected:
	 *   post:
	 *     summary: Executes hook ShippingMethodSelected
	 *     description: Executes hook ShippingMethodSelected
	 *     requestBody:
	 *       description: Data with selected shipping method
	 *       content:
	 *         application/json:
	 *           schema:
	 *             type: object
	 *     responses:
	 *       200:
	 *         description: Success
	 */
	async applePayOnShippingMethodSelected(
		eventObj,
	): Promise<
		| {lineItems: Array<TApplePayJS.ApplePayLineItem>; total: TApplePayJS.ApplePayLineItem}
		| {error: string; status: string}
	> {
		return await this.post(
			this.generateEndpointUrl('__SYSTEM__ApplePay-ShippingMethodSelected', true),
			convertApplePayEventObject(eventObj),
		).then((response: Response) => response.json());
	}

	/**
	 * @swagger
	 * /SGGooglePay-PaymentAuthorized:
	 *   post:
	 *     summary: Authorize payment for Google pay
	 *     description: Authorize payment for Google pay
	 *     requestBody:
	 *       description: Email, Tokenization data, Selected shipping and billing addresses
	 *       content:
	 *         application/json:
	 *           schema:
	 *             type: object
	 *     responses:
	 *       200:
	 *         description: Success
	 */
	async googlePayPaymentAuthorized(params): Promise<any> {
		return await this.post(
			this.generateEndpointUrl('SGGooglePay-PaymentAuthorized', true),
			{
				email: params.email,
				shippingAddress: params.shippingAddress,
				billingAddress: params.billingAddress,
				shippingOptionData: params.shippingOptionData,
				tokenizationData: params.tokenizationData,
				dfReferenceId: params.dfReferenceId,
				basketId: params.basketId
			}
		).then((response: Response) => response.json());
	}

	/**
	 * @swagger
	 * /ApplePay-Validate:
	 *   post:
	 *     summary: Validates Apple Pay data
	 *     description: Validates Apple Pay data
	 *     requestBody:
	 *       description: Selected shipping and billing addresses
	 *       content:
	 *         application/json:
	 *           schema:
	 *             type: object
	 *     responses:
	 *       200:
	 *         description: Success
	 */
	async applePayValidation(eventObj): Promise<{
		validationResult: {
			shippingContact: TApplePayContactValidationResult;
			billingContact: TApplePayContactValidationResult;
		};
	}> {
		return await this.post(
			this.generateEndpointUrl('ApplePay-Validate', true),
			convertApplePayEventObject(eventObj),
		).then((response: Response) => response.json());
	}

	/**
	 * @swagger
	 * /AmazonPay-Applicable:
	 *   post:
	 *     summary: Get Amazon applicable response data
	 *     description: Get Amazon applicable response data
	 *     responses:
	 *       200:
	 *         description: Success
	 */
	async amazonPayApplicable(): Promise<any> {
		return await this.get(this.generateEndpointUrl('AmazonPay-Applicable', true)).then((response: Response) =>
			response.json(),
		);
	}

	/**
	 * @swagger
	 * /Signifyd-GetSessionID:
	 *   get:
	 *     summary: Get Signifyd session id
	 *     description: Get Signifyd session id
	 *     content:
	 *       application/json:
	 *         schema:
	 *           type: object
	 *     responses:
	 *       200:
	 *         description: Success
	 */
	async getSignifydSessionID(): Promise<any> {
		return await this.get(this.generateEndpointUrl('Signifyd-GetSessionID', true)).then((response: Response) =>
			response.json(),
		);
	}

	/**
	 * @swagger
	 * /ReturnAuthorization-Create:
	 *   post:
	 *     summary: Creates RA custom object
	 *     description: Creates RA custom object
	 *     requestBody:
	 *       description: Data to create RA custom object
	 *       content:
	 *         application/json:
	 *           schema:
	 *             type: object
	 *     responses:
	 *       200:
	 *         description: Success
	 */
	async createReturnAuthorization(payload): Promise<any> {
		return await this.post(this.generateEndpointUrl('ReturnAuthorization-Create', true), payload, {}, false).then(
			(response: Response) => response.json(),
		);
	}

	/**
	 * @swagger
	 * /Order-GetByCustomerEmail:
	 *   post:
	 *     summary: Receives an order for a guest customer
	 *     description: Receives an order for a guest customer
	 *     requestBody:
	 *       description: Order number and email
	 *       content:
	 *         application/json:
	 *           schema:
	 *             type: object
	 *     responses:
	 *       200:
	 *         description: Success
	 */
	async getGuestReturnAuthorizationOrder(queryParams): Promise<any> {
		return await this.get(this.generateEndpointUrl('Order-GetByCustomerEmail', true), queryParams, true).then(
			(response: Response) => {
				return response.json();
			},
		);
	}

	/**
	 * @swagger
	 * /Order-GetByZipCode:
	 *   post:
	 *     summary: Receives an order for a guest customer by zip code
	 *     description: Receives an order for a guest customer
	 *     requestBody:
	 *       description: Order number and zip code
	 *       content:
	 *         application/json:
	 *           schema:
	 *             type: object
	 *     responses:
	 *       200:
	 *         description: Success
	 */
	async getOrderByZipCode(orderNo: string, zipCode: string): Promise<any> {
		return await this.get(
			this.generateEndpointUrl('Order-GetByZipCode', true),
			{
				orderNo,
				zipCode,
			},
			false,
		).then((response: Response) => response.json());
	}

	/**
	 * @swagger
	 * /Countries-GetAll:
	 *   get:
	 *     summary: Retrieves all countries
	 *     description: Retrieves all countries
	 *     responses:
	 *       200:
	 *         description: Success
	 */
	async getAllCountries(): Promise<any> {
		return await this.get(this.generateEndpointUrl('Countries-GetAll', true), {}, true).then((response: Response) =>
			response.json(),
		);
	}

	/**
	 * @swagger
	 * /ReturnAuthorization-GetReturnReasons:
	 *   get:
	 *     summary: Retrieves return reasons
	 *     description: Retrieves return reasons
	 *     responses:
	 *       200:
	 *         description: Success
	 */
	async getReturnReasons(): Promise<any> {
		return await this.get(this.generateEndpointUrl('ReturnAuthorization-GetReturnReasons', true), {}, true).then(
			(response: Response) => response.json(),
		);
	}

	/**
	 * @swagger
	 * /ReturnAuthorization-PreValidate:
	 *   post:
	 *     summary: Validates order for being returnable
	 *     description: Validates order for being returnable
	 *     requestBody:
	 *       description: Data to create RA custom object
	 *       content:
	 *         application/json:
	 *           schema:
	 *             type: object
	 *     responses:
	 *       200:
	 *         description: Success
	 */
	async preValidateReturnAuthorization(payload): Promise<any> {
		return await this.post(
			this.generateEndpointUrl('ReturnAuthorization-PreValidate', true),
			payload,
			{},
			false,
		).then((response: Response) => response.json());
	}

	/**
	 * @swagger
	 * /Locales-GetAll:
	 *   get:
	 *     summary: Retrieves all locales
	 *     description: Retrieves all locales
	 *     responses:
	 *       200:
	 *         description: Success
	 */
	async getAllLocales(): Promise<any> {
		return await this.get(this.generateEndpointUrl('Locales-GetAll', true), {}, true).then((response: Response) =>
			response.json(),
		);
	}

	/**
	 * @swagger
	 * /Currencies-getCurrencyConversionRate:
	 *   get:
	 *     summary: Retrieves conversion rates
	 *     description: Retrieves conversion rates
	 *     parameters:
	 *      - in: query
	 *        name: from
	 *        description: Currency code to convert from
	 *        required: true
	 *        type: string
	 *      - in: query
	 *        name: to
	 *        description: Currency codes to convert to
	 *        required: true
	 *        type: array
	 *     responses:
	 *       200:
	 *         description: Success
	 */
	async getCurrencyConversionRate(from: String, to: String[]): Promise<any> {
		return await this.get(this.generateEndpointUrl('Currencies-GetConversionRates', true), {from, to}, true).then(
			(response: Response) => response.json(),
		);
	}

	/**
	 * @swagger
	 * /IdentityProvider-OAuth:
	 *   post:
	 *     summary: Retrieves identity provider auth data
	 *     description: Retrieves identity provider auth data
	 *     parameters:
	 *      - in: query
	 *        name: body
	 *        description: Identity provider token and usid
	 *        required: true
	 *        type: object
	 *     responses:
	 *       200:
	 *         description: Success
	 */
	async identityProviderLogin(body: object): Promise<any> {
		return await this.post(this.generateEndpointUrl('IdentityProvider-OAuth', true), body).then(
			(response: Response) => response.json(),
		);
	}

	/**
	 * @swagger
	 * /IdentityProvider-Authenticate:
	 *   post:
	 *     summary: Retrieves identity provider auth data without instant register
	 *     description: Retrieves identity provider auth data without instant register
	 *     parameters:
	 *      - in: query
	 *        name: body
	 *        description: Identity provider token and usid
	 *        required: true
	 *        type: object
	 *     responses:
	 *       200:
	 *         description: Success
	 */
	async authenticateProviderLogin(body: object): Promise<any> {
		return await this.post(this.generateEndpointUrl('IdentityProvider-Authenticate', true), body).then(
			(response: Response) => response.json(),
		);
	}

	/**
	 * @swagger
	 * /IdentityProvider-Register:
	 *   post:
	 *     summary: Register provided external user to SFCC
	 *     description: Register provided external user to SFCC
	 *     parameters:
	 *      - in: query
	 *        name: body
	 *        description: Identity provider token and usid
	 *        required: true
	 *        type: object
	 *     responses:
	 *       200:
	 *         description: Success
	 */
	async registerExternalProfile(body: object): Promise<any> {
		return await this.post(this.generateEndpointUrl('IdentityProvider-Register', true), body).then(
			(response: Response) => response.json(),
		);
	}

	/**
	 * @swagger
	 * /Customer-LoginOnBehalf:
	 *   post:
	 *     summary: Retrieves access token for login on behalf
	 *     description: Retrieves access token for login on behalf
	 *     requestBody:
	 *       description: login id for on behalf login
	 *       content:
	 *         application/json:
	 *           schema:
	 *             type: object
	 *     responses:
	 *       200:
	 *         description: Success
	 */
	async getLoginOnBehalfToken(loginId: string): Promise<any> {
		return await this.post(this.generateEndpointUrl('Customer-LoginOnBehalf', true), {loginId}).then(
			(response: Response) => response.json(),
		);
	}

	/**
	 * @swagger
	 * /IdentityProvider-ReOAuth:
	 *   post:
	 *     summary: Refreshes identity provider auth data
	 *     description: Refreshes identity provider auth data
	 *     parameters:
	 *      - in: query
	 *        name: body
	 *        description: Identity provider id and type
	 *        required: true
	 *        type: object
	 *     responses:
	 *       200:
	 *         description: Success
	 */
	async refreshIdentityProviderLogin(body: object): Promise<any> {
		return await this.post(this.generateEndpointUrl('IdentityProvider-ReOAuth', true), body).then(
			(response: Response) => response.json(),
		);
	}

	/**
	 * @swagger
	 * /QuickSearch-GetCategories:
	 *   get:
	 *     summary: Retrieves categories for search flyout
	 *     description: Retrieves categories for search flyout
	 *     parameters:
	 *      - in: query
	 *        name: q
	 *        description: Query string
	 *        required: true
	 *        type: string
	 *      - in: query
	 *        name: customerPersonalizationKey
	 *        description: Customer groups joined by pipe
	 *        required: true
	 *        type: string
	 *     responses:
	 *       200:
	 *         description: Success
	 */
	async getQuickSearchCategories(q: String, customerPersonalizationKey: string): Promise<any> {
		return await this.get(
			this.generateEndpointUrl('QuickSearch-GetCategories', true),
			{q, customerPersonalizationKey},
			true,
		).then((response: Response) => response.json());
	}

	/**
	 * @swagger
	 * /System-Log:
	 *   post:
	 *     summary: Creates a new NotifyMe record.
	 *     description: If a record for the provided email already exists, it is updated.
	 *     security:
	 *       - csrf: []
	 *     requestBody:
	 *       content:
	 *         application/json:
	 *           schema:
	 *             required:
	 *               - level
	 *               - type
	 *               - title
	 *               - message
	 *             type: object
	 *             properties:
	 *               level:
	 *                 type: string
	 *                 example: error|info|debug|warning
	 *               type:
	 *                 type: string
	 *                 example: server|client
	 *               title:
	 *                 type: string
	 *                 example: Error occurred
	 *               message:
	 *                 type: object
	 *                 example: test@test.com
	 *     responses:
	 *       200:
	 *         description: Success
	 */
	async log(level: LogLevel, type: LogType, title: string, stackTrace: object): Promise<any> {
		return await this.post(this.generateEndpointUrl('System-Log', true), {
			level,
			type,
			title,
			stackTrace,
		});
	}

	/**
	 * @swagger
	 * /Stores-FindAll:
	 *   get:
	 *     summary: Retrieves stores
	 *     description: Retrieves stores
	 *     body:
	 *       description: Data to retrieve stores.
	 *       content:
	 *         application/json:
	 *           schema:
	 *             type: object
	 *     responses:
	 *       200:
	 *         description: Success
	 */
	async getStores(params): Promise<any> {
		return await this.get(this.generateEndpointUrl('Stores-FindAll', true), params).then((response: Response) =>
			response.json(),
		);
	}

	/**
	 * @swagger
	 * /Stores-Get:
	 *   get:
	 *     summary: Retrieves store by store id
	 *     description: Retrieves store by store id
	 *     parameters:
	 *      - in: query
	 *        name: storeId
	 *        description: Query string
	 *        required: true
	 *        type: string
	 *     responses:
	 *       200:
	 *         description: Success
	 */
	async getStore(params): Promise<any> {
		return await this.get(this.generateEndpointUrl('Stores-Get', true), params).then((response: Response) =>
			response.json(),
		);
	}

	/**
	 * @swagger
	 * /Appointments-Create:
	 *   post:
	 *     summary: Book an appointment
	 *     description: Submit new book appointment
	 *     requestBody:
	 *       content:
	 *         application/json:
	 *           schema:
	 *             type: object
	 *     responses:
	 *       200:
	 *         description: Success
	 */
	async bookAnAppointment(params): Promise<any> {
		return await this.post(this.generateEndpointUrl('Appointments-Create', true), params).then(
			(response: Response) => response.json(),
		);
	}

	/**
	 * @swagger
	 * /Reservations-Create:
	 *   post:
	 *     summary: Create reservation
	 *     description: Submit new reservation in store
	 *     requestBody:
	 *       content:
	 *         application/json:
	 *           schema:
	 *             type: object
	 *     responses:
	 *       200:
	 *         description: Success
	 */
	async createReservation(params: CreateReservationRequest): Promise<any> {
		return await this.post(this.generateEndpointUrl('Reservations-Create', true), params).then(
			(response: Response) => response.json(),
		);
	}

	/**
	 * @swagger
	 * /Reservations-FindAvailableStores:
	 *   post:
	 *     summary: find stores for specific products
	 *     description: find stores for specific products
	 *     requestBody:
	 *       content:
	 *         application/json:
	 *           schema:
	 *             type: object
	 *     responses:
	 *       200:
	 *         description: Success
	 */
	async findAvailableStores(params: FindAvailableStoresRequest): Promise<FindAvailableStoresResponse> {
		return await this.post(this.generateEndpointUrl('Reservations-FindAvailableStores', true), params).then(
			(response: Response) => response.json(),
		);
	}

	/**
	 * @swagger
	 * /ShareProduct-ShareByEmail:
	 *   post:
	 *     summary: Share product via email
	 *     description: Share product via email
	 *     body:
	 *       description: Data to share product.
	 *       content:
	 *         application/json:
	 *           schema:
	 *             type: object
	 *     responses:
	 *       200:
	 *         description: Success
	 */
	async shareProductByEmail(params): Promise<any> {
		return await this.post(this.generateEndpointUrl('ShareProduct-ShareByEmail', true), params).then(
			(response: Response) => response.json(),
		);
	}

	/**
	 * @swagger
	 * /Wishlist-ShareByEmail:
	 *   post:
	 *     summary: Share wishlist via email
	 *     description: Share wishlist via email
	 *     body:
	 *       description: Data to share wishlist.
	 *       content:
	 *         application/json:
	 *           schema:
	 *             type: object
	 *     responses:
	 *       200:
	 *         description: Success
	 */
	async shareWishlistByEmail(params): Promise<any> {
		return await this.post(this.generateEndpointUrl('Wishlist-ShareByEmail', true), params).then(
			(response: Response) => response.json(),
		);
	}

	/**
	 * @swagger
	 * /SLASSessionHelper-SyncSession:
	 *   get:
	 *     summary: synchronizes the session between pwa and SFRA
	 *     description: synchronizes the session between pwa and SFRA
	 *     responses:
	 *       200:
	 *         description: Success
	 */
	async syncSession(basketId, force = false): Promise<any> {
		return await this.get(
			this.generateEndpointUrl('SLASSessionHelper-SyncSession', true),
			{basketId, force},
			false,
			false,
		).then((response: Response) => response.json());
	}

	/**
	 * @swagger
	 * /TrackingConsent-SetConsent:
	 *   post:
	 *     summary: Sets tracking consent preference for the current session
	 *     description: Sets tracking consent preference for the current session
	 *     security:
	 *       - csrf: []
	 *     requestBody:
	 *       content:
	 *         application/json:
	 *           schema:
	 *             required:
	 *               - allowed
	 *             type: object
	 *             properties:
	 *               allowed:
	 *                 type: boolean
	 *                 description: "Whether tracking is allowed (true) or not (false)"
	 *     responses:
	 *       200:
	 *         description: Success
	 */
	async setTrackingConsent(allowed: boolean): Promise<any> {
		return await this.post(this.generateEndpointUrl('TrackingConsent-SetConsent', true), {
			allowed: allowed,
		}).then((response: Response) => response.json());
	}
}

/**
 * Generates a querystring from an object
 * @param {Object} obj The object to be used
 * @returns {String}
 */
const generateQueryString = (obj: object) => {
	const str = [];

	for (let p in obj) {
		if (obj.hasOwnProperty(p)) {
			str.push(encodeURIComponent(p) + '=' + encodeURIComponent(obj[p]));
		}
	}

	return str.join('&');
};

/**
 * Returns event as a plain object
 * @param eventObj Event object
 * @return Object
 */
const convertApplePayEventObject = (eventObj: object) => {
	const filteredEvent = {};

	for (const prop in eventObj) {
		if (!Event.prototype.hasOwnProperty(prop)) {
			filteredEvent[prop] = eventObj[prop];
		}
	}

	return filteredEvent;
};

export default SFCCAPI;
